import React from 'react';
import axios from 'axios';
import { makeStyles } from '@material-ui/styles';
import { Box } from '@material-ui/core';
import { CRM_UPDATES_CHANGED } from './crmUpdatesAccess';

// "What's new" running strip under the navbar (2026-10-22). Lives inside the
// fixed AppBar so it never scrolls with the page. Items are published from
// Profile → Publish CRM Updates and targeted server-side by role / cohort /
// WhatsApp team (GET /api/ai/announcements/active).
const AI_PATH = (process.env.REACT_APP_AI_PATH || '').trim();
const POLL_MS = 5 * 60 * 1000;
export const CRM_STRIP_HEIGHT = 30;

const useStyles = makeStyles(() => ({
    bar: {
        display: 'flex',
        alignItems: 'center',
        height: CRM_STRIP_HEIGHT,
        width: '100%',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        background: '#1E2A4A',
        color: '#FFFFFF',
        fontSize: 13,
        borderTop: '1px solid rgba(0,0,0,0.06)'
    },
    track: { flex: 1, overflow: 'hidden', height: '100%', display: 'flex', alignItems: 'center' },
    marquee: {
        display: 'inline-block',
        paddingLeft: '100%',
        animationName: '$scroll',
        animationTimingFunction: 'linear',
        animationIterationCount: 'infinite',
        '&:hover': { animationPlayState: 'paused' }
    },
    '@keyframes scroll': {
        '0%': { transform: 'translateX(0)' },
        '100%': { transform: 'translateX(-100%)' }
    },
    item: { marginRight: 56, fontWeight: 500 },
    tag: {
        marginLeft: 4,
        padding: '1px 5px',
        borderRadius: 4,
        background: '#F5A623',
        color: '#1E2A4A',
        fontSize: 9,
        fontWeight: 700,
        textTransform: 'uppercase',
        verticalAlign: 'super'
    }
}));

const fmtDate = (d) => {
    try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); } catch (e) { return ''; }
};

// leftOffset = open desktop sidebar width, so the strip starts where the
// sidebar ends instead of running above it.
const CrmUpdateStrip = ({ onVisibleChange, leftOffset = 0 }) => {
    const classes = useStyles();
    const [items, setItems] = React.useState([]);

    React.useEffect(() => {
        if (!AI_PATH) return undefined;
        let cancelled = false;
        const load = async () => {
            try {
                const res = await axios.get(`${AI_PATH}/api/ai/announcements/active`);
                if (!cancelled && res.data?.success) setItems(res.data.items || []);
            } catch (e) { /* silent */ }
        };
        load();
        const iv = setInterval(load, POLL_MS);
        window.addEventListener(CRM_UPDATES_CHANGED, load);
        return () => { cancelled = true; clearInterval(iv); window.removeEventListener(CRM_UPDATES_CHANGED, load); };
    }, []);

    const visible = items.length > 0;
    React.useEffect(() => { if (onVisibleChange) onVisibleChange(visible); }, [visible, onVisibleChange]);
    if (!visible) return null;

    // ~7 chars/sec feels readable; never faster than 20s per loop.
    const chars = items.reduce((n, i) => n + (i.text || '').length + 12, 0);
    const duration = Math.max(20, Math.round(chars / 7));

    return (
        <Box className={classes.bar} style={{ marginLeft: leftOffset, width: `calc(100% - ${leftOffset}px)`, transition: 'margin-left 225ms, width 225ms' }}>
            <div className={classes.track}>
                <span className={classes.marquee} style={{ animationDuration: `${duration}s` }}>
                    {items.map((i) => (
                        <span key={i.id} className={classes.item}>
                            {i.text}
                            <sup className={classes.tag}>{i.tag || fmtDate(i.createdAt)}</sup>
                        </span>
                    ))}
                </span>
            </div>
        </Box>
    );
};

export default CrmUpdateStrip;
